<script setup>
import { ref, watch, onMounted } from 'vue';
import { gsap } from 'gsap';
import snakeCase from 'lodash-es/snakeCase';
import each from 'lodash-es/each';
import { useRouter } from 'vue-router';

import { GA } from '@/mixins/Ga';
// import { useStore } from '@/store/Store.js';
import wysiwyg from '@/components/wysiwyg/Wysiwyg.vue';
import { useLenis } from '@/components/scroll/Lenis.js';

const props = defineProps({
    space: { type: Boolean, default: true },
    center: { type: Boolean, default: false },
    color: {
        type: String,
        default: '',
    },
    themeColor: {
        type: String,
        default: '',
    },
    sections: { type: Object, default: () => {} },
    title: {
        type: String,
        default: '',
    },
    icon: {
        type: String,
        default: '',
    },
    parentElement: {
        type: Object,
        default: () => {},
    },
});

const lenis = useLenis();
// const store = useStore();
const currentAccordion = ref(null);
const router = useRouter();
const rootEl = ref(null);
const ga = new GA({ category: 'midBody', action: 'clicInteract' });

const snakeIt = (title) => snakeCase(title);

const close = () => {
    router.replace({ hash: null }).catch(() => {});
    each(props.parentElement.querySelectorAll('.Faq__list.is-faq-active'), (e) => e.classList.remove('is-faq-active'));
    gsap.to('.Faq__answer', 1, { height: 0, ease: 'expo.out' });
    currentAccordion.value = null;
};

const toggle = (el, list) => {
    if (el.classList.contains('is-faq-active')) {
        close();
        return;
    }

    close();

    if (list) {
        router.replace({ hash: `#${snakeIt(list.question)}` }).catch(() => {});
        currentAccordion.value = list.question;
    }

    el.classList.add('is-faq-active');
    gsap.to(el.querySelector('.Faq__answer'), 1, { height: 'auto', ease: 'expo.out' });
};

watch(currentAccordion, (newVal, oldVal) => {
    if (newVal) ga.send({ label: `open - ${newVal}`, camelCase: false });

    if (oldVal) ga.send({ label: `close - ${oldVal}`, camelCase: false });
});

onMounted(() => {
    if (window.location.hash) {
        const elname = window.location.hash.replace('#', '');
        const el = document.querySelector(`[name='${elname}']`);
        if (el) {
            lenis.value.scrollTo(el.offsetTop - 160, {
                duration: 1.5,
                onComplete: () => {
                    const activeList = props.sections.filter((list) => snakeIt(list.question) === elname);
                    if (activeList.length) {
                        currentAccordion.value = activeList[0].question;
                        toggle(el, activeList[0]);
                    }
                },
            });
        }
    }
});
</script>

<template>
    <div
        ref="rootEl"
        class="FaqSection"
    >
        <div
            class="Faq__title pb-4 md:pb-14"
            :class="['text-dark-' + props.themeColor]"
        >
            <h2 class="flex flex-wrap items-center text-4xl font-bold">
                <template v-if="props.icon">
                    <span
                        class="mr-6 inline-block w-[115px]"
                        v-html="props.icon"
                    />
                </template>
                {{ props.title }}
            </h2>
        </div>
        <div class="Faq__list-container groupnot flex flex-wrap justify-between">
            <div
                v-for="section in props.sections"
                :key="section.question"
                class="Faq__list group relative mt-[-1px] w-full cursor-pointer border-y md:w-[48%]"
                :name="snakeIt(section.question)"
                :class="['border-light-' + props.themeColor, 'last:border-dark-' + props.themeColor, 'last:border-light-' + props.themeColor]"
                v-on:click="
                    (e) => {
                        toggle(e.currentTarget, section);
                    }
                "
            >
                <div class="Faq__list-inner py-2 md:py-5">
                    <div
                        class="Faq__bg absolute -inset-x-8 inset-y-[-2px] left-0 max-w-[93vw] translate-x-[-2.5%] rounded-xl opacity-0 transition duration-500 md:max-w-[inherit]"
                        :class="['bg-dark-' + props.themeColor, 'bg-' + props.themeColor]"
                    />
                    <svg
                        class="Faq__icon absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 fill-current transition-all duration-500 can-hover:group-hover:!opacity-100 can-hover:groupnot-hover:opacity-50"
                        :class="[
                            currentAccordion !== section.question ? 'text-dark-' + props.themeColor : null,
                            {
                                'text-white': currentAccordion === section.question,
                            },
                        ]"
                        viewBox="0 0 20 20"
                    >
                        <path d="M19,9H11V1A1,1,0,0,0,9,1V9H1a1,1,0,0,0,0,2H9v8a1,1,0,0,0,2,0V11h8a1,1,0,0,0,0-2Z" />
                    </svg>
                    <div
                        class="Faq__question relative pr-12 transition duration-500 xl:pr-24 can-hover:group-hover:translate-x-6 can-hover:group-hover:opacity-100 can-hover:groupnot-hover:opacity-50"
                        :class="[
                            currentAccordion !== section.question ? 'text-dark-' + props.themeColor : null,
                            {
                                'text-white': currentAccordion === section.question,
                            },
                        ]"
                    >
                        <h6
                            class="text-xl font-bold"
                            v-html="section.question"
                        />
                    </div>
                    <div
                        class="Faq__answer relative h-0 overflow-hidden opacity-0 transition duration-500 xl:px-6"
                        :class="[
                            currentAccordion === section.question ? 'text-dark-' + props.themeColor : null,
                            {
                                'text-white': currentAccordion === section.question,
                            },
                        ]"
                    >
                        <wysiwyg
                            :space="false"
                            color="dark"
                            :data="{ data: section }"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
